import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UserService } from '../../../../services/user.service';
import {
  DedupeMatch,
  LocationsService,
  PlaceDetail,
  SuburbPlace,
} from '../../../../services/locations.service';
import { GoogleLookup, googleFacts, stars } from './google-place';
import {
  CONDUCTOR_OUT_DONE,
  ConductorOutAction,
  PlaceRequests,
  RESTORED,
  canManageConductor,
  canOpenProviders,
  conductorOutQuestion,
  toggleDismissed,
} from './place-actions';
import { BLOCK_LABEL, BLOCK_OPTIONS, BlockType, blockTypeFor, kindLabel } from './place-kinds';

/**
 * The popup over a place's marker on the Suburbs map (#253 step 3; Ivan 2026-10-16): what it is, Google's rating
 * (live, never stored), the key facts, and one click to put it on the website or add it to Conductor. "Details" opens
 * the full place panel (rename, notes, history).
 *
 * Add to Conductor runs the dedupe gate (#248) first: with no likely duplicate it adds straight away; otherwise the
 * candidates show here, to dismiss one by one or to open.
 */
@Component({
  selector: 'app-suburb-place-popup',
  templateUrl: './suburb-place-popup.component.html',
  styleUrls: ['./suburb-place-popup.component.scss'],
})
export class SuburbPlacePopupComponent implements OnChanges {
  @Input() place!: SuburbPlace;
  @Input() readOnly = false;
  /** Start adding to Conductor as soon as it opens (the Places list's one-click button). */
  @Input() autoAdd = false;
  /** Sitting below its marker (near the top of the map), and how far it was moved sideways to stay on the map. */
  @Input() below = false;
  @Input() arrowShift = 0;
  @Output() changed = new EventEmitter<PlaceDetail>();
  @Output() details = new EventEmitter<number>();
  @Output() closed = new EventEmitter<void>();

  detail: PlaceDetail | null = null;
  google: GoogleLookup | undefined;
  message = '';
  blockType: BlockType = 'venue';
  /** The dedupe gate's answer while adding: the providers it may already be, and which are dismissed. */
  matches: DedupeMatch[] | null = null;
  dismissed = new Set<number>();

  readonly blockOptions = BLOCK_OPTIONS;
  readonly blockLabel = BLOCK_LABEL;
  readonly kindLabel = kindLabel;
  readonly stars = stars;

  /** Remove from / restore to Conductor, stop using for new bookings: Content Managers and Operations (Ivan 2026-10-16). */
  readonly canManageConductor: boolean;
  /** Links to a provider's locations: not for content-only accounts (no Service Providers for them). */
  readonly canOpenProviders: boolean;
  private readonly requests: PlaceRequests;

  constructor(
    private api: LocationsService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    users: UserService
  ) {
    this.canManageConductor = canManageConductor(users);
    this.canOpenProviders = canOpenProviders(users);
    this.requests = new PlaceRequests(zone, () => cdr.detectChanges());
  }

  get busy(): boolean {
    return this.requests.busy;
  }

  get error(): string {
    return this.requests.error;
  }

  ngOnChanges(c: SimpleChanges): void {
    if (
      c.place &&
      (c.place.previousValue as SuburbPlace | undefined)?.locationId !== this.place?.locationId
    ) {
      this.detail = null;
      this.google = undefined;
      this.requests.error = '';
      this.message = '';
      this.matches = null;
      this.dismissed = new Set();
      this.blockType = blockTypeFor(this.place?.kind);
      this.load();
    }
  }

  private load(): void {
    const p = this.place;
    if (!p) return;
    this.requests.run(this.api.place(p.locationId), (d) => {
      this.detail = d;
      if (this.autoAdd && !d.provider) d.removedProvider ? this.restore() : this.addToConductor();
    });
    googleFacts({
      locationId: p.locationId,
      googlePlaceId: p.googlePlaceId,
      name: p.crawlName || p.name,
      lat: p.lat,
      lng: p.lng,
    }).then((g) =>
      this.zone.run(() => {
        if (this.place?.locationId !== p.locationId) return;
        this.google = g;
        this.cdr.detectChanges();
      })
    );
  }

  get addressLine(): string {
    const a = this.detail?.address;
    return a ? [a.street, a.suburb].filter(Boolean).join(', ') : '';
  }

  get googleClosed(): string | null {
    const g = this.google;
    if (!g || g === 'unavailable') return null;
    return g.status === 'CLOSED_PERMANENTLY'
      ? 'Google says it has closed permanently'
      : g.status === 'CLOSED_TEMPORARILY'
        ? 'Google says it is closed for now'
        : null;
  }

  get googleFacts() {
    return this.google && this.google !== 'unavailable' ? this.google : null;
  }

  private done(d: PlaceDetail, message: string): void {
    this.detail = d;
    this.message = message;
    this.changed.emit(d);
  }

  // ------------------------------------------------------------------ the website, one click

  addToWebsite(): void {
    const d = this.detail;
    if (!d) return;
    this.requests.run(this.api.setListing(d.locationId, 'add'), (r) =>
      this.done(r, 'On the website from the next publish of the suburbs near it.')
    );
  }

  removeFromWebsite(): void {
    const d = this.detail;
    if (!d || !window.confirm(`Take "${d.name}" off the website?`)) return;
    this.requests.run(this.api.setListing(d.locationId, 'remove'), (r) =>
      this.done(r, 'Off the website from the next publish of the suburbs near it.')
    );
  }

  // ------------------------------------------------------------------ out of Conductor

  /** Remove (no arrangement uses it), stop using it for new bookings, or bring it back. Asks first. */
  conductorOut(action: ConductorOutAction): void {
    const d = this.detail;
    if (!d?.provider || !window.confirm(conductorOutQuestion(action, d))) return;
    this.requests.run(this.api.setConductor(d.locationId, action), (r) =>
      this.done(r, CONDUCTOR_OUT_DONE[action])
    );
  }

  /** A provider removed from this place comes back: the same record (soft delete's way back). */
  restore(): void {
    const d = this.detail;
    if (!d?.removedProvider) return;
    this.requests.run(this.api.setConductor(d.locationId, 'restore'), (r) =>
      this.done(r, RESTORED)
    );
  }

  // ------------------------------------------------------------------ Conductor, one click (after the dedupe gate)

  addToConductor(): void {
    const d = this.detail;
    if (!d || d.provider) return;
    if (this.matches) return this.addNow();
    this.requests.run(this.api.onboardCheck(d.locationId), (r) => {
      this.matches = r.matches;
      if (!r.matches.length) this.addNow();
    });
  }

  toggleDismiss(m: DedupeMatch): void {
    toggleDismissed(this.dismissed, m.serviceId);
  }

  get canAddAnyway(): boolean {
    return !!this.matches && this.matches.every((m) => !m.hard && this.dismissed.has(m.serviceId));
  }

  private addNow(): void {
    const d = this.detail;
    if (!d || !this.canAddAnyway) return;
    this.requests.run(
      this.api.onboard({
        locationId: d.locationId,
        blockType: this.blockType,
        name: d.name,
        notDuplicateOf: [...this.dismissed],
      }),
      () => {
        this.matches = null;
        this.requests.run(this.api.place(d.locationId), (r) =>
          this.done(
            r,
            `Added to Conductor as a ${BLOCK_LABEL[this.blockType].toLowerCase()} (it starts tagged "Hide From Website").`
          )
        );
      }
    );
  }
}
